// apps/web/src/pages/estoque/localizacao-sheet.tsx — Fase 13, Plano 09 (EST-07, D-21).
// FormDialog "Nova localização" / "Editar localização". Nome duplicado, versão desatualizada e demais erros de negócio
// mostram a mensagem do contrato sem fechar o FormDialog. A autorização real é do servidor (estoque:configurar).
import { useEffect, useState, type FormEvent } from 'react';
import type { CriarLocalizacaoInput, EditarLocalizacaoInput, LocalizacaoView } from '@pieltec/contracts';
import { Button } from '../../components/ui/button';
import { FormMessage } from '../../components/ui/form-message';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { FormDialog, FormDialogContent, FormDialogDescription, FormDialogFooter, FormDialogHeader, FormDialogTitle } from '../../components/ui/form-dialog';
import { apiMutate } from '../../lib/csrf-fetch';
import { mensagemErroLocalizacao } from '../../lib/estoque/localizacoes';
import { lerErroApi } from '../../lib/vendas/erro-api';

interface LocalizacaoSheetProps {
  readonly aberto: boolean;
  readonly onAbertoChange: (aberto: boolean) => void;
  /** null = "Nova localização"; preenchida = "Editar localização" (envia a versão lida). */
  readonly localizacao: LocalizacaoView | null;
  readonly onSalvo: () => void;
  /** 409 de versão: a lista é recarregada para o usuário reabrir a localização já atualizada. */
  readonly onConflito?: () => void;
}

const LIMITE_NOME = 80;
const LIMITE_DESCRICAO = 200;

export function LocalizacaoSheet({ aberto, onAbertoChange, localizacao, onSalvo, onConflito }: LocalizacaoSheetProps) {
  const edicao = localizacao !== null;
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [erroNome, setErroNome] = useState<string | null>(null);
  const [erroApi, setErroApi] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!aberto) return;
    setNome(localizacao?.nome ?? '');
    setDescricao(localizacao?.descricao ?? '');
    setErroNome(null);
    setErroApi(null);
  }, [aberto, localizacao]);

  async function salvar(event: FormEvent): Promise<void> {
    event.preventDefault();
    const nomeLimpo = nome.trim();
    if (nomeLimpo === '') {
      setErroNome('Informe o nome da localização.');
      return;
    }
    setErroNome(null);
    setErroApi(null);
    setSalvando(true);
    const descricaoLimpa = descricao.trim() === '' ? null : descricao.trim();
    try {
      const res = localizacao
        ? await apiMutate(`/api/v1/estoque/localizacoes/${localizacao.id}`, {
            method: 'PATCH',
            body: { versao: localizacao.versao, nome: nomeLimpo, descricao: descricaoLimpa } satisfies EditarLocalizacaoInput,
          })
        : await apiMutate('/api/v1/estoque/localizacoes', {
            body: { nome: nomeLimpo, descricao: descricaoLimpa } satisfies CriarLocalizacaoInput,
          });
      if (!res.ok) {
        const erro = await lerErroApi(res, 'Não foi possível salvar a localização.');
        setErroApi(mensagemErroLocalizacao(erro.codigo) ?? erro.mensagem);
        if (erro.codigo === 'LOCALIZACAO_VERSAO_DESATUALIZADA') onConflito?.();
        return;
      }
      onSalvo();
      onAbertoChange(false);
    } catch {
      setErroApi('Não foi possível salvar a localização. Verifique a conexão e tente novamente.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <FormDialog open={aberto} onOpenChange={onAbertoChange}>
      <FormDialogContent>
        <form onSubmit={(e) => void salvar(e)} className="flex h-full flex-col gap-4" noValidate>
          <FormDialogHeader>
            <FormDialogTitle>{edicao ? 'Editar localização' : 'Nova localização'}</FormDialogTitle>
            <FormDialogDescription>Prateleiras, câmara fria e outros lugares onde o material fica guardado.</FormDialogDescription>
          </FormDialogHeader>

          <div className="flex flex-col gap-4 px-4">
            <div className="flex flex-col gap-1">
              <Label htmlFor="localizacao-nome">Nome</Label>
              <Input
                id="localizacao-nome"
                value={nome}
                maxLength={LIMITE_NOME}
                onChange={(e) => setNome(e.target.value)}
                aria-invalid={erroNome !== null || erroApi !== null}
              />
              {erroNome && <FormMessage>{erroNome}</FormMessage>}
            </div>

            <div className="flex flex-col gap-1">
              <Label htmlFor="localizacao-descricao">Descrição (opcional)</Label>
              <Input id="localizacao-descricao" value={descricao} maxLength={LIMITE_DESCRICAO} onChange={(e) => setDescricao(e.target.value)} />
            </div>

            {erroApi && (
              <div role="alert">
                <FormMessage>{erroApi}</FormMessage>
              </div>
            )}
          </div>

          <FormDialogFooter>
            <Button type="submit" disabled={salvando}>
              {salvando ? 'Salvando...' : 'Salvar localização'}
            </Button>
            <Button type="button" variant="outline" onClick={() => onAbertoChange(false)}>
              Voltar
            </Button>
          </FormDialogFooter>
        </form>
      </FormDialogContent>
    </FormDialog>
  );
}
